/**
 * Work items - admin over `client > application > project > feature`.
 *
 * This is where Ryan builds the month's work before anyone touches the board.
 * The board shows tasks moving; this shows the structure they move inside, so
 * the screen is a tree rather than four lists: the parent of a thing is the
 * most useful fact about it, and a flat table throws that away.
 *
 * Four list queries rather than one call per expanded node. The whole tree for
 * one agency is a few hundred rows; fetching it once means expanding a node is
 * instant and no cache key depends on what happens to be open.
 */
import { useMemo, useState } from 'react';

import type { FeatureDto } from '@botguide/shared';

import { Button, Card, EmptyState, ErrorState, Field, LoadingScreen, PageHeader, Stat } from '@/components';
import { DeleteEntityDialog, EntityDialog } from '@/components/admin/EntityDialog';
import { SetUpClientDialog } from '@/components/admin/SetUpClientDialog';
import { TaskDialog } from '@/components/board/TaskDialog';
import type { EntityFormRequest } from '@/components/admin/EntityDialog';
import {
  EntityTree,
  buildTree,
  nodeKey,
  totalsFor,
} from '@/components/admin/EntityTree';
import type { ApplicationNode, ClientNode, CreateTarget, ProjectNode, TreeTarget } from '@/components/admin/EntityTree';
import { formatCount, formatPoints } from '@/lib/format';
import { useApplications, useClients, useFeatures, useProjects } from '@/lib/queries';
import { useCan } from '@/lib/session';

export default function WorkItems() {
  const clients = useClients();
  const applications = useApplications();
  const projects = useProjects();
  const features = useFeatures();

  const canCreate = useCan('create', 'client');
  const canUpdate = useCan('update', 'client');

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<EntityFormRequest | null>(null);
  const [doomed, setDoomed] = useState<TreeTarget | null>(null);
  // Onboarding takes all four levels at once - F8. Editing one node is still
  // `EntityDialog`; these are different jobs and stay different components.
  const [settingUp, setSettingUp] = useState(false);
  /*
   * Adding a task from the tree, through the board's own dialog.
   *
   * A task is not one of this screen's four entities - it carries a lane, a
   * stage, an assignee and story points - so it uses `TaskDialog` rather than
   * `EntityDialog`. Two task forms would drift apart, and this screen's job is
   * the tree, not a second opinion about what a task is.
   */
  const [addingTaskTo, setAddingTaskTo] = useState<string | null>(null);

  const tree = useMemo(
    () =>
      buildTree(
        clients.data ?? [],
        applications.data ?? [],
        projects.data ?? [],
        features.data ?? [],
      ),
    [clients.data, applications.data, projects.data, features.data],
  );

  const query = search.trim().toLowerCase();
  const visible = useMemo(() => (query ? filterTree(tree, query) : tree), [tree, query]);

  /*
   * While a search is running the matches are what you asked for, so the tree
   * opens itself to them. Collapsing back to the user's own expansion when the
   * box is cleared is the behaviour that surprises nobody.
   */
  const openKeys = useMemo(
    () => (query ? new Set(allKeys(visible)) : expanded),
    [query, visible, expanded],
  );

  const totals = useMemo(() => summarise(tree), [tree]);

  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  /** Creating a child of a collapsed node should leave you looking at it. */
  const openCreate = (target: CreateTarget) => {
    if (target.kind !== 'client') {
      const parentKind =
        target.kind === 'application' ? 'client' : target.kind === 'project' ? 'application' : 'project';
      setExpanded((current) => new Set(current).add(`${parentKind}:${target.parentId}`));
    }
    setForm({ mode: 'create', target });
  };

  const error = clients.error ?? applications.error ?? projects.error ?? features.error;
  const loading =
    clients.isPending || applications.isPending || projects.isPending || features.isPending;

  const refetchAll = () => {
    void clients.refetch();
    void applications.refetch();
    void projects.refetch();
    void features.refetch();
  };

  return (
    <>
      <PageHeader
        title="Work items"
        description="Clients, applications, projects and features — the tree the board hangs off. Deleting a node deletes everything under it."
        actions={
          canCreate ? (
            <Button variant="primary" iconLeft="plus" onClick={() => setSettingUp(true)}>
              New client
            </Button>
          ) : null
        }
        toolbar={
          tree.length > 0 ? (
            <div className="flex flex-col gap-4">
              <Card padding="md" className="rise">
                <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 lg:grid-cols-5">
                  <Stat label="Clients" value={totals.clients} />
                  <Stat label="Applications" value={totals.applications} />
                  <Stat label="Projects" value={totals.projects} />
                  <Stat label="Features" value={totals.features} />
                  <Stat
                    label="Tasks"
                    value={totals.tasks}
                    hint={
                      totals.blocked > 0
                        ? `${formatCount(totals.blocked, 'task')} blocked`
                        : `${formatPoints(totals.totalPoints)} points planned`
                    }
                    tone={totals.blocked > 0 ? 'var(--danger)' : undefined}
                  />
                </div>
              </Card>

              <div className="flex flex-wrap items-end gap-3">
                <Field
                  label="Find"
                  type="search"
                  placeholder="Client, application, project or feature"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  wrapperClassName="min-w-0 flex-1 sm:max-w-80"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={Boolean(query)}
                    onClick={() => setExpanded(new Set(allKeys(tree)))}
                  >
                    Expand all
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={Boolean(query)}
                    onClick={() => setExpanded(new Set<string>())}
                  >
                    Collapse
                  </Button>
                </div>
              </div>
            </div>
          ) : null
        }
      />

      {loading ? (
        <LoadingScreen label="Loading the work tree" />
      ) : error ? (
        <ErrorState error={error} onRetry={refetchAll} />
      ) : tree.length === 0 ? (
        <EmptyState
          icon="work"
          title="No clients yet"
          description="Everything in the portal hangs off a client: applications, then monthly projects, then the features the board's tasks belong to."
          action={
            canCreate ? (
              <Button variant="primary" iconLeft="plus" onClick={() => setSettingUp(true)}>
                Add the first client
              </Button>
            ) : null
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState
          icon="search"
          title={`Nothing matches “${search.trim()}”`}
          description="The search looks at the names of clients, applications, projects and features."
          action={
            <Button size="sm" variant="secondary" onClick={() => setSearch('')}>
              Clear the search
            </Button>
          }
        />
      ) : (
        <EntityTree
          clients={visible}
          expanded={openKeys}
          onToggle={toggle}
          canWrite={canUpdate}
          onCreate={openCreate}
          onCreateTask={(feature) => setAddingTaskTo(feature.id)}
          onEdit={(target) => setForm({ mode: 'edit', target })}
          onDelete={setDoomed}
        />
      )}

      {/*
        Keyed on the request so a second dialog starts from a clean form rather
        than from whatever the last one held.
      */}
      {form ? (
        <EntityDialog key={requestKey(form)} request={form} onClose={() => setForm(null)} />
      ) : null}

      {settingUp ? <SetUpClientDialog onClose={() => setSettingUp(false)} /> : null}

      {addingTaskTo ? (
        <TaskDialog
          key={addingTaskTo}
          task={null}
          open
          initialFeatureId={addingTaskTo}
          onClose={() => setAddingTaskTo(null)}
        />
      ) : null}

      {doomed ? (
        <DeleteEntityDialog
          key={nodeKey(doomed)}
          target={doomed}
          onClose={() => setDoomed(null)}
        />
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Derivation                                                                 */
/* -------------------------------------------------------------------------- */

function requestKey(request: EntityFormRequest): string {
  if (request.mode === 'edit') return `edit:${nodeKey(request.target)}`;
  const parent = request.target.kind === 'client' ? 'root' : request.target.parentId;
  return `create:${request.target.kind}:${parent}`;
}

function summarise(tree: readonly ClientNode[]) {
  let applications = 0;
  let projects = 0;
  let features = 0;
  let tasks = 0;
  let blocked = 0;
  let totalPoints = 0;

  for (const node of tree) {
    const totals = totalsFor({ kind: 'client', node });
    applications += totals.applications;
    projects += totals.projects;
    features += totals.features;
    tasks += totals.tasks;
    blocked += totals.blocked;
    totalPoints += totals.totalPoints;
  }

  return { clients: tree.length, applications, projects, features, tasks, blocked, totalPoints };
}

/** Every node key in the tree, for expand-all and for search. */
function allKeys(tree: readonly ClientNode[]): string[] {
  const keys: string[] = [];
  for (const client of tree) {
    keys.push(nodeKey({ kind: 'client', node: client }));
    for (const application of client.applications) {
      keys.push(nodeKey({ kind: 'application', node: application }));
      for (const project of application.projects) {
        keys.push(nodeKey({ kind: 'project', node: project }));
      }
    }
  }
  return keys;
}

function matches(entity: { name: string }, query: string): boolean {
  return entity.name.toLowerCase().includes(query);
}

/**
 * Keep a node when it matches, or when anything under it does.
 *
 * A matching parent keeps all of its children, so searching a client name
 * gives you that client's whole tree rather than a client with nothing in it.
 */
function filterTree(tree: readonly ClientNode[], query: string): ClientNode[] {
  const keptClients: ClientNode[] = [];

  for (const client of tree) {
    const clientMatches = matches(client.client, query);
    const applications: ApplicationNode[] = [];

    for (const application of client.applications) {
      const applicationMatches = clientMatches || matches(application.application, query);
      const projects: ProjectNode[] = [];

      for (const project of application.projects) {
        const projectMatches = applicationMatches || matches(project.project, query);
        const features: FeatureDto[] = projectMatches
          ? project.features
          : project.features.filter((feature) => matches(feature, query));

        if (projectMatches || features.length > 0) projects.push({ project: project.project, features });
      }

      if (applicationMatches || projects.length > 0) {
        applications.push({ application: application.application, projects });
      }
    }

    if (clientMatches || applications.length > 0) {
      keptClients.push({ client: client.client, applications });
    }
  }

  return keptClients;
}
